import type { RefObject } from "react";
import { useMemo, useImperativeHandle } from "react";
import type { CalendarDate } from "@internationalized/date";
import { startOfMonth } from "@internationalized/date";

export type CalendarHandle = {
  scrollToMonth: (
    date: CalendarDate,
    options?: {
      behavior?: ScrollBehavior;
      block?: ScrollLogicalPosition;
      inline?: ScrollLogicalPosition;
    },
  ) => void;
  isMonthInView: (
    date: CalendarDate,
    options?: { partial?: boolean; tolerance?: number },
  ) => boolean;
};
export function useCalendarHandle(
  calendarReference: RefObject<CalendarHandle | null> | undefined,
  containerReference: RefObject<HTMLDivElement | null>,
  months: CalendarDate[],
): CalendarHandle {
  const handle: CalendarHandle = useMemo(
    () => ({
      isMonthInView: (
        date: CalendarDate,
        options?: { partial?: boolean; tolerance?: number },
      ) => {
        const container = containerReference.current;
        if (!container || months.length === 0) {
          return false;
        }

        const partial = options?.partial !== false; // default true
        const tolerance = options?.tolerance ?? 2;

        const target = startOfMonth(date);
        const index = months.findIndex(
          (m) => m.year === target.year && m.month === target.month,
        );
        if (index === -1) {
          return false;
        }

        const monthNodes = container.querySelectorAll("[data-month]");
        const monthElement = monthNodes.item(index) as HTMLElement | null;
        if (!monthElement) {
          return false;
        }

        const containerRect = container.getBoundingClientRect();
        const monthRect = monthElement.getBoundingClientRect();

        // Horizontal visibility within the scroller viewport
        const fullyVisible =
          monthRect.left >= containerRect.left + tolerance &&
          monthRect.right <= containerRect.right - tolerance;
        const partiallyVisible =
          monthRect.left < containerRect.right - tolerance &&
          monthRect.right > containerRect.left + tolerance;

        return partial ? partiallyVisible : fullyVisible;
      },
      scrollToMonth: (date: CalendarDate, options) => {
        const container = containerReference.current;
        if (!container || months.length === 0) {
          return;
        }

        const target = startOfMonth(date);
        const index = months.findIndex(
          (m) => m.year === target.year && m.month === target.month,
        );
        if (index === -1) {
          return;
        }

        const monthNodes = container.querySelectorAll("[data-month]");
        const monthElement = monthNodes.item(index) as HTMLElement | null;
        if (!monthElement) {
          return;
        }

        // Align month to the left edge of the horizontal scroller on desktop.
        // block: 'nearest' avoids vertical jump; inline: 'start' left-aligns.
        monthElement.scrollIntoView({
          behavior: options?.behavior ?? "smooth",
          block: options?.block ?? "nearest",
          inline: options?.inline ?? "start",
        });
      },
    }),
    [containerReference, months],
  );

  // months in deps so handle stays in sync with visible month list
  useImperativeHandle(calendarReference, () => handle, [months]);

  return handle;
}
